import { useEffect, useState, useCallback } from 'react';
import './PaymentBar.css';
import PaymentModal from './PaymentModal';
import * as api from '../lib/api';
import type { User } from '../lib/types';
import { fmtNum } from '../lib/format';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://backend.heads-up-poker.ru';

interface PaymentBarProps {
  /** Temporarily hide the bar (a startup popup is on screen). */
  hidden?: boolean;
  userId: number;
}

export default function PaymentBar({ userId, hidden = false }: PaymentBarProps) {
  const [unpaidData, setUnpaidData] = useState<{ total: number; count: number; orders: any[] } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  // Recovery: if user was redirected mid-payment, check pending payment from localStorage
  const recoverPendingPayment = useCallback(async () => {
    const raw = localStorage.getItem('pending_payment');
    if (!raw) return;
    try {
      const { payment_id, order_ids } = JSON.parse(raw);
      const res = await fetch(`${BACKEND_URL}/api/payments/status/${payment_id}`);
      const data = await res.json();
      if (data.status === 'succeeded') {
        localStorage.removeItem('pending_payment');
        await Promise.all((order_ids as number[]).map(id => api.markOrderPaid(id, true)));
      } else if (data.status === 'canceled') {
        localStorage.removeItem('pending_payment');
      }
    } catch {
      // ignore recovery errors
    }
  }, []);

  const loadUnpaid = useCallback(() => {
    if (!userId || userId <= 0) return;
    api.getUnpaidOrders(userId)
      .then(data => {
        if (data.count > 0) {
          setUnpaidData({ total: data.total, count: data.count, orders: data.orders });
        } else {
          setUnpaidData(null);
        }
      })
      .catch(() => setUnpaidData(null));
  }, [userId]);

  useEffect(() => {
    // First recover any pending payment from previous session, then load
    recoverPendingPayment().finally(() => loadUnpaid());
    // Load user for PaymentModal
    api.getUserById(userId).then(setUser).catch(() => {});

    let interval = setInterval(loadUnpaid, 15000);
    const onVisibility = () => {
      clearInterval(interval);
      if (document.visibilityState === 'visible') {
        loadUnpaid();
        interval = setInterval(loadUnpaid, 15000);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadUnpaid, userId]);

  // Lets fixed bottom UI (e.g. the tournament page's register bar) sit above this bar.
  useEffect(() => {
    document.body.classList.toggle('has-paybar', !!unpaidData && !hidden);
    return () => document.body.classList.remove('has-paybar');
  }, [unpaidData, hidden]);

  if (!unpaidData) return null;

  return (
    <>
      {!hidden && (
        <div className="pay-bar">
          <button className="pay-bar__btn" onClick={() => setShowModal(true)}>
            <span className="pay-bar__label">
              Оплатить заказы
              <span className="pay-bar__count">{unpaidData.count}</span>
            </span>
            <span className="pay-bar__total">{fmtNum(unpaidData.total)} ₽</span>
          </button>
        </div>
      )}

      {showModal && user && (
        <PaymentModal
          user={user}
          orders={unpaidData.orders}
          total={unpaidData.total}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            setUnpaidData(null);
            loadUnpaid();
          }}
        />
      )}
    </>
  );
}
